import { StatCardGrid, type LabeledStat } from '@/components/common/StatCardGrid';
import { formatTime } from '@/utils/date';
import { EMPTY_VALUE, TAKA_SYMBOL } from '@/constants/app.constants';
import type { MapOn } from '../constants/moTravel.constants';
import type { MoTravelRoute, MoTravelSummary } from '../types/moTravel.types';
import { hasGpsPoints } from '../utils/dayRoutes';
import { formatDuration } from '../utils/formatDuration';
import { formatKmValue } from '../utils/formatKm';
import { isMultiDayPeriod } from '../utils/moTravelPeriod';

const KM_UNIT = 'km';
const NO_GPS_LABEL = 'No GPS';

type MoRouteStats = Pick<
  MoTravelSummary,
  | 'startTime'
  | 'lastActivity'
  | 'totalEntry'
  | 'institutesCovered'
  | 'totalMinutes'
  | 'routeKm'
  | 'totalBill'
>;

const EMPTY_DAY_STATS: MoRouteStats = {
  startTime: null,
  lastActivity: null,
  totalEntry: 0,
  institutesCovered: 0,
  totalMinutes: null,
  routeKm: 0,
  totalBill: 0,
};

function getStats(route: MoTravelRoute, statsDate: string | null): MoRouteStats {
  if (!statsDate) return route.summary;
  return route.days.find((day) => day.date === statsDate) ?? EMPTY_DAY_STATS;
}

function getRouteCoveredText(route: MoTravelRoute, stats: MoRouteStats, statsDate: string | null) {
  if (stats.routeKm > 0) return formatKmValue(stats.routeKm);
  if (stats.totalEntry === 0) return EMPTY_VALUE;
  const daysInScope = statsDate ? route.days.filter((day) => day.date === statsDate) : route.days;
  return hasGpsPoints(daysInScope) ? EMPTY_VALUE : NO_GPS_LABEL;
}

const formatTotalTime = (totalMinutes: number | null): string =>
  totalMinutes === null ? EMPTY_VALUE : formatDuration(totalMinutes);

interface MoRouteSummaryCardsProps {
  route: MoTravelRoute | undefined;
  mapOn: MapOn;
  statsDate: string | null;
  isLoading: boolean;
  isError: boolean;
}

export function MoRouteSummaryCards({
  route,
  mapOn,
  statsDate,
  isLoading,
  isError,
}: MoRouteSummaryCardsProps) {
  const stats = route ? getStats(route, statsDate) : undefined;
  const routeCoveredText =
    route && stats ? getRouteCoveredText(route, stats, statsDate) : undefined;
  const isWholeMultiDayPeriod = isMultiDayPeriod(mapOn) && statsDate === null;
  const routeStats: LabeledStat[] = [
    {
      key: 'startTime',
      label: isWholeMultiDayPeriod ? 'First Login' : 'Logged In',
      value: formatTime(stats?.startTime ?? null),
    },
    {
      key: 'lastActivity',
      label: 'Last Activity',
      value: formatTime(stats?.lastActivity ?? null),
    },
    {
      key: 'totalEntry',
      label: 'Total Entry',
      value: stats?.totalEntry,
    },
    {
      key: 'institutesCovered',
      label: 'Institutes Covered',
      value: stats?.institutesCovered,
    },
    {
      key: 'totalTime',
      label: 'Total Time',
      value: stats && formatTotalTime(stats.totalMinutes),
    },
    {
      key: 'routeCovered',
      label: 'Route Covered',
      value: routeCoveredText,
      unit: stats && stats.routeKm > 0 ? KM_UNIT : undefined,
    },
    {
      key: 'totalBill',
      label: 'Total Bill',
      value: stats?.totalBill,
      unit: TAKA_SYMBOL,
    },
  ];

  return <StatCardGrid stats={routeStats} isLoading={isLoading} isError={isError} />;
}
